// i18next-extract-mark-ns-start monei-pay

import {ContactSalesButton} from 'components/ContactSalesButton';
import {Content} from 'components/Content';
import {List} from 'components/List';
import 'keen-slider/keen-slider.min.css';
import {Section, SectionActions, SectionHeader, SectionImage} from 'components/Section';
import {SEO} from 'components/SEO';
import {SignUpButton} from 'components/SignUpButton';
import {PageProps, graphql} from 'gatsby';
import {Trans, useI18next} from 'gatsby-plugin-react-i18next';

//Images
import monei_pay_2 from 'images/ATELIER-MONEI-checkout-transparent.png';
import {Flex} from 'components/Flex';
import React from 'react';
import {DownloadOnAppleStore} from 'components/DownloadOnAppleStore';
import {DownloadOnGooglePlay} from 'components/DownloadOnGooglePlay';
import Bold from 'components/Bold';
import {AnchorLink} from 'components/AnchorLink';
import {MoneiVideoSection} from 'components/landings/MoneiVideoSection';
import {CompatibleWith} from 'components/monei-pay/CompatibleWith';
import IndexBackground from 'components/monei-pay/IndexBackground';
import {BlogLink} from 'components/links/Blog';
import {StyledDiv} from 'components/StyledSx';
import {Card, CardWrapper} from 'components/Card';
import CardsContainer from 'components/CardsContainer';
import {QrGroup} from 'components/monei-pay/QrGroup';
import {HorizontalScreenshotsNew} from 'components/monei-pay/HorizontalScreenshotsNew';
import {MainTitleContent} from 'components/monei-pay/MainTitleContent';
import {InternalPageLink} from 'components/links/Pages';
import {SupportLink} from 'components/links/Support';
import styled from 'styled-components';
import {GoogleReviews, Place} from 'components/GoogleReviews';
import {
  GradientBox,
  GradientCtaImage,
  GradientSectionDescription
} from 'components/landings/GradientBox';
import {ButtonLink} from 'components/Button';
import integrated_payments_header_image from 'images/integrated_payments_header_image.png';

const CompTableWrapper = styled.div`
  overflow-x: auto;
`;

const ComparisonTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  font-size: 0.9rem;
  thead tr {
    background: #f7f8fa;
  }
  th {
    padding: 14px 20px;
    text-align: left;
    font-weight: 700;
    font-size: 0.8rem;
    letter-spacing: 0.04em;
    color: #3d4251;
  }
  th:last-child {
    background: #f7f8fa;
    color: #3d4251;
  }
  td {
    padding: 14px 20px;
    border-top: 1px solid #e5e7eb;
    color: #3d4251;
  }
  td:first-child {
    font-weight: 600;
    color: #0a0e1a;
  }
  td.good {
    color: #3d4251;
    font-weight: 400;
  }
  td.neutral {
    color: #6b7280;
  }
  td:last-child {
    background: #ffffff;
  }
  tr:hover td {
    background: #f7f8fa;
  }
  @media (max-width: 576px) {
    font-size: 0.8rem;
    th,
    td {
      padding: 10px 12px;
    }
  }
`;

const TestimonialSection = styled.div`
  display: flex;
  gap: 48px;
  align-items: flex-start;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const TestimonialText = styled.div`
  flex: 1;
  font-style: italic;
  font-size: 1.05rem;
  line-height: 1.7;
  color: #3d4251;
`;

const VideoWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const VideoCaption = styled.p`
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0;
  text-align: center;
`;

const VideoEmbed = styled.div`
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
  border-radius: 8px;
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const UseCasesGrid = styled(CardsContainer)`
  row-gap: 20px;
  justify-content: center;
`;

const UseCaseCard = styled(Card)`
  padding: 32px;
  align-items: flex-start;
  text-align: left;
  flex: 1 0 calc(33.333% - 14px);
  max-width: calc(33.333% - 14px);
  color: #0a0e1a;

  h4 {
    max-width: none;
    font-size: 1.15rem;
    margin-bottom: 0.5rem;
    font-weight: 350;
    color: #0a0e1a;
  }

  p {
    color: #6b7280;
    margin: 0;
  }

  a {
    color: inherit !important;
    text-decoration: underline !important;
  }

  @media (max-width: 960px) {
    flex: 1 0 calc(50% - 10px);
    max-width: calc(50% - 10px);
  }

  @media (max-width: 768px) {
    flex: 1 0 100%;
    max-width: 100%;
  }
`;

const PayWayCard = styled(Card)`
  padding: 32px;
  align-items: flex-start;
  text-align: left;
  flex: 1 0 calc(50% - 10px);
  max-width: calc(50% - 10px);

  h4 {
    max-width: none;
    font-size: 1.15rem;
    margin-bottom: 0.5rem;
  }

  @media (max-width: 768px) {
    flex: 1 0 100%;
    max-width: 100%;
  }
`;

const PayWaysContainer = styled(CardsContainer)`
  row-gap: 20px;
`;

const MoneiPay: React.FC<PageProps> = (props) => {
  const {t, language} = useI18next();
  const data = props.data as any;
  const place: Place = {
    rating: data.googlePlacesPlace.rating,
    user_ratings_total: data.googlePlacesPlace.user_ratings_total,
    reviews: data.allGooglePlacesReview.nodes
  };
  const ReviewsBackground = styled.div`
    position: relative;
    margin-top: 100px;

    &:before {
      display: block;
      content: '';
      z-index: -1;
      height: 70%;
      position: absolute;
      transform: skew(0, -9deg);
      left: 0;
      right: 0;
      bottom: 0;
      top: 20%;
      background: linear-gradient(96.34deg, #00e9d5 0%, #7984d9 100%);
    }
  `;

  return (
    <>
      <SEO
        title={t('Hardware Free POS System')}
        description={t(
          'Avoid a bulky and unreliable POS and save on transaction fees by accepting payments from' +
            " your phone. Use MONEI's hardware free POS system ››"
        )}
      />

      <IndexBackground>
        <Content>
          <Section columnLg>
            <MainTitleContent sx={{maxWidth: {all: '590px', lg: '100%'}}}>
              <SectionHeader underline tagName="h1">
                <Trans>Get a simple, hardware free POS system</Trans>
              </SectionHeader>
              <Trans parent="p">
                Tired of expensive card terminals, short battery life, patchy connectivity, and
                never having enough devices for your staff? Skip the bulky hardware and accept
                in-person payments straight from your phone or tablet with MONEI's mobile POS app.
              </Trans>
              <SectionActions>
                <SignUpButton variant="light" style={{marginRight: 15}}>
                  <Trans>Open an Account</Trans>
                </SignUpButton>
                <ContactSalesButton />
                <CompatibleWith />
              </SectionActions>
            </MainTitleContent>
            <MoneiVideoSection />
          </Section>
        </Content>
      </IndexBackground>
      <Content>
        <Section
          column
          centered
          textAlign="center"
          sx={{paddingTop: '64px', paddingBottom: '64px'}}>
          <SectionHeader underline style={{marginBottom: '40px'}}>
            <Trans>One app. Four ways to get paid.</Trans>
          </SectionHeader>
          <PayWaysContainer>
            <PayWayCard>
              <h4>
                <AnchorLink href="https://monei.com/features/tap-to-pay-iphone/" target="_self">
                  <Trans>Tap to Pay on iPhone</Trans>
                </AnchorLink>
              </h4>
              <CardWrapper style={{maxWidth: 'none'}}>
                <Trans parent="p">
                  Turn any iPhone XS or newer into a contactless terminal. Customers tap their card,
                  Apple Pay, or another NFC wallet directly on your iPhone--no readers, no adaptors,
                  no extra hardware.
                </Trans>
              </CardWrapper>
            </PayWayCard>
            <PayWayCard>
              <h4>
                <InternalPageLink slug="features/tap-to-phone">
                  <Trans>Tap to Phone on Android</Trans>
                </InternalPageLink>
              </h4>
              <CardWrapper style={{maxWidth: 'none'}}>
                <Trans parent="p">
                  Accept contactless cards and digital wallet payments by tapping the customer's
                  card or phone against your Android device using the MONEI POS app.
                </Trans>
              </CardWrapper>
            </PayWayCard>
            <PayWayCard>
              <h4>
                <InternalPageLink slug="monei-pay/qr">
                  <Trans>QR code payments</Trans>
                </InternalPageLink>
              </h4>
              <CardWrapper style={{maxWidth: 'none'}}>
                <Trans parent="p">
                  Open the app, enter the amount, and show a QR code. Customers can scan to pay with
                  cards, Apple Pay, Google Pay, Bizum, or PayPal—no app download on their end.
                </Trans>
              </CardWrapper>
            </PayWayCard>
            <PayWayCard>
              <h4>
                <InternalPageLink slug="features/pay-by-link">
                  <Trans>Payment requests</Trans>
                </InternalPageLink>
              </h4>
              <CardWrapper style={{maxWidth: 'none'}}>
                <Trans parent="p">
                  Enter the amount, tap Send payment request, and type your customer's mobile number
                  or email address. If the number is registered with Bizum, they get a push
                  notification to confirm directly in their banking app.
                </Trans>
              </CardWrapper>
            </PayWayCard>
          </PayWaysContainer>
        </Section>
      </Content>
      <Content sx={{marginTop: {md: '20px'}}}>
        <Section centered sx={{paddingTop: '20px', gap: '16px'}}>
          <SectionImage
            style={{width: '420px'}}
            mobileWidth={370}
            src={monei_pay_2}
            alt={language === 'es' ? 'Aplicación TPV de MONEI' : 'MONEI POS App'}
            title="Scan to Pay"
            className="hide-on-mobile"
          />
          <div>
            <SectionHeader underline style={{marginTop: '10px'}}>
              <Trans>Save time and money by accepting payments from any mobile device</Trans>
            </SectionHeader>
            <Trans parent="p">
              Eliminate traditional POS hardware costs and save counter space, with MONEI's simple,
              secure, and affordable mobile POS app. Reduce costs by up to 50% compared to
              traditional providers, with no monthly hardware rental and only{' '}
              <AnchorLink href="https://monei.com/pricing/" target="_self">
                per-transaction fees
              </AnchorLink>
              .
            </Trans>
            <p>
              <Bold>
                <Trans>Boost sales with more payment methods</Trans>
              </Bold>
            </p>
            <Trans parent="p">
              Stop losing customers because you don't offer their preferred payment options. Accept
              the methods consumers already use: credit and debit cards,{' '}
              <AnchorLink href="https://monei.com/payment-methods/apple-pay/" target="_self">
                Apple Pay
              </AnchorLink>
              ,{' '}
              <AnchorLink href="https://monei.com/payment-methods/google-pay/" target="_self">
                Google Pay
              </AnchorLink>
              ,{' '}
              <AnchorLink href="https://monei.com/payment-methods/paypal/" target="_self">
                PayPal
              </AnchorLink>
              , and{' '}
              <AnchorLink href="https://monei.com/payment-methods/bizum/" target="_self">
                Bizum
              </AnchorLink>{' '}
              (Spain).
            </Trans>
            <p>
              <Bold>
                <Trans>Improve customer experience by taking payments anywhere</Trans>
              </Bold>
            </p>
            <Trans parent="p">
              Whether you have a retail store, restaurant, or process payments on the go, MONEI's
              mobile payment app lets you and your staff provide the best possible customer
              experience by meeting shoppers where they are.
            </Trans>
            <Trans parent="p">👇 Download the app.</Trans>
            <Flex>
              <div style={{marginRight: '16px'}}>
                <DownloadOnAppleStore />
              </div>
              <DownloadOnGooglePlay />
            </Flex>
          </div>
        </Section>
      </Content>
      <Content>
        <Section textAlign="center" column centered sx={{width: '100%', gap: '12px'}}>
          <SectionHeader underline>
            <Trans style={{fontSize: '2rem'}}>How does the MONEI POS app work?</Trans>
          </SectionHeader>
          <Trans parent="p" style={{marginBottom: '16px'}}>
            Open your MONEI's POS app, select “contactless payment”, and enter the purchase amount.
            Then your customer taps their physical chip card or digital wallet or scans a QR code to
            complete their purchase.
          </Trans>
        </Section>
      </Content>
      <Content sx={{maxWidth: '991px'}}>
        <HorizontalScreenshotsNew />
      </Content>
      <Content>
        <Section textAlign="center" column centered sx={{width: '100%', gap: '12px'}}>
          <SectionHeader underline>
            <Trans>MONEI POS App vs. Traditional POS Systems</Trans>
          </SectionHeader>
          <Trans parent="p" style={{marginBottom: '8px'}}>
            Same outcome—your customer pays, you get the money—but very different setup, cost, and
            flexibility. Here's how the two approaches compare side by side.
          </Trans>
          <CompTableWrapper>
            <ComparisonTable
              aria-label={t('Comparison between MONEI POS App and Traditional POS Systems')}>
              <thead>
                <tr>
                  <th scope="col">{t('Feature')}</th>
                  <th scope="col">{t('MONEI POS App')}</th>
                  <th scope="col">{t('Traditional POS')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{t('Hardware')}</td>
                  <td className="good">{t('None. Use a mobile or tablet you already have.')}</td>
                  <td className="neutral">{t('A dedicated terminal at each checkout.')}</td>
                </tr>
                <tr>
                  <td>{t('Fixed costs')}</td>
                  <td className="good">{t('No hardware rental or maintenance fees.')}</td>
                  <td className="neutral">{t('Monthly rental and/or maintenance fees.')}</td>
                </tr>
                <tr>
                  <td>{t('Mobility')}</td>
                  <td className="good">
                    {t('Charge at any point in your store, restaurant, or wherever you are.')}
                  </td>
                  <td className="neutral">{t('Limited to where the terminal is plugged in.')}</td>
                </tr>
                <tr>
                  <td>{t('Payment methods')}</td>
                  <td className="good">
                    <Trans>
                      Cards, Apple Pay, Google Pay, PayPal, Bizum, plus contactless payments with{' '}
                      <InternalPageLink slug="features/tap-to-pay-iphone">
                        Tap to Pay on iPhone
                      </InternalPageLink>{' '}
                      and{' '}
                      <InternalPageLink slug="features/tap-to-phone">
                        Tap to Phone on Android
                      </InternalPageLink>
                      .
                    </Trans>
                  </td>
                  <td className="neutral">
                    {t(
                      'Cards and contactless; usually without Bizum, PayPal, or account-to-account payment options.'
                    )}
                  </td>
                </tr>
                <tr>
                  <td>{t('Activation time')}</td>
                  <td className="good">
                    {t('Online signup in minutes: no shipping or on-site installation.')}
                  </td>
                  <td className="neutral">
                    {t('Hardware order, shipping, and physical installation.')}
                  </td>
                </tr>
                <tr>
                  <td>{t('Multiple devices')}</td>
                  <td className="good">
                    {t(
                      'Add the app to all the mobiles and tablets your team needs, at no additional hardware cost.'
                    )}
                  </td>
                  <td className="neutral">
                    {t('One device per terminal, and each has an extra cost.')}
                  </td>
                </tr>
                <tr>
                  <td>{t('Analytics')}</td>
                  <td className="good">
                    {t('Real-time dashboard with transaction data, reports, and reconciliation.')}
                  </td>
                  <td className="neutral">{t('Often limited to a printed daily summary.')}</td>
                </tr>
              </tbody>
            </ComparisonTable>
          </CompTableWrapper>
        </Section>
      </Content>

      <Content>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '15px',
            marginBottom: '40px',
            marginTop: '2.5rem'
          }}>
          <SignUpButton variant="light">
            <Trans>Open an Account</Trans>
          </SignUpButton>
          <ContactSalesButton />
        </div>
      </Content>
      <Content>
        <Section column>
          <SectionHeader underline tagName="h2" sx={{alignSelf: 'center', textAlign: 'center'}}>
            <Trans>
              Incapto experiences a 66% uplift in coffee cup orders by switching to MONEI
            </Trans>
          </SectionHeader>
          <TestimonialSection>
            <TestimonialText>
              <Trans>
                Before switching to MONEI, customers experienced connection issues with the
                machines, failed payments, and friction due to the limited availability of
                alternative payment options for completing transactions.
              </Trans>
              <br />
              <br />
              <AnchorLink href="https://monei.com/blog/incapto/">
                <Trans>Read the success story</Trans>
              </AnchorLink>
            </TestimonialText>
            <VideoWrapper>
              <VideoEmbed>
                <iframe
                  src="https://www.youtube.com/embed/FXVR5Mj5kS0"
                  title={t('Incapto success story with MONEI')}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </VideoEmbed>
              <VideoCaption>
                <Trans>Video by Incapto</Trans>
              </VideoCaption>
            </VideoWrapper>
          </TestimonialSection>
        </Section>
      </Content>

      <Content>
        <Section column>
          <SectionHeader underline tagName="h2" sx={{alignSelf: 'center', textAlign: 'center'}}>
            <Trans>What types of businesses use MONEI's mobile POS app?</Trans>
          </SectionHeader>
          <UseCasesGrid>
            <UseCaseCard>
              <h4>
                <Trans>E-commerce &amp; brick-and-mortar shops</Trans>
              </h4>
              <Trans parent="p">
                Whether you occasionally take your{' '}
                <AnchorLink href="https://monei.com/online-payments/" target="_self">
                  online business
                </AnchorLink>{' '}
                offline, run a boutique, or operate multiple locations, speed up your{' '}
                <AnchorLink href="https://monei.com/monei-pay/retail-POS-system/" target="_self">
                  retail checkout
                </AnchorLink>
                , improve the customer experience, and track{' '}
                <AnchorLink href="https://monei.com/omnichannel-payments-platform/" target="_self">
                  omnichannel sales
                </AnchorLink>
                {'—'}all from a phone or tablet.
              </Trans>
            </UseCaseCard>
            <UseCaseCard>
              <h4>
                <Trans>Food &amp; hospitality</Trans>
              </h4>
              <Trans parent="p">
                Mobile payments for{' '}
                <AnchorLink
                  href="https://monei.com/monei-pay/qr-code-payment-restaurant/"
                  target="_self">
                  restaurants
                </AnchorLink>{' '}
                give you more flexibility than a fixed terminal. Take payments at the table, at the
                bar, at an event, or at a{' '}
                <AnchorLink
                  href="https://monei.com/monei-pay/food-truck-pos-system/"
                  target="_self">
                  food truck
                </AnchorLink>{' '}
                stop.
              </Trans>
            </UseCaseCard>
            <UseCaseCard>
              <h4>
                <Trans>Service providers &amp; mobile businesses</Trans>
              </h4>
              <Trans parent="p">
                From{' '}
                <AnchorLink
                  href="https://monei.com/monei-pay/legal-billing-software/"
                  target="_self">
                  legal billing
                </AnchorLink>{' '}
                to{' '}
                <AnchorLink href="https://monei.com/monei-pay/pos-for-freelancers/" target="_self">
                  freelance
                </AnchorLink>{' '}
                web designers to artists, MONEI's POS app is the most flexible card-present setup
                for all types of service providers in Spain.
              </Trans>
            </UseCaseCard>
            <UseCaseCard>
              <h4>
                <Trans>Taxis</Trans>
              </h4>
              <Trans parent="p">
                Accepting{' '}
                <AnchorLink
                  href="https://monei.com/monei-pay/pos-systems-for-taxis/"
                  target="_self">
                  card payments in taxis
                </AnchorLink>{' '}
                is now required in most Spanish cities. Replace your old dataphone and save time and
                money by using your smartphone as a taxi POS.
              </Trans>
            </UseCaseCard>
            <UseCaseCard>
              <h4>
                <Trans>Hair and beauty salons</Trans>
              </h4>
              <Trans parent="p">
                Make payments more convenient and affordable at your{' '}
                <AnchorLink
                  href="https://monei.com/monei-pay/pos-systems-for-hair-salon/"
                  target="_self">
                  hair or beauty salon
                </AnchorLink>{' '}
                with MONEI's mobile POS app.
              </Trans>
            </UseCaseCard>
            <UseCaseCard>
              <h4>
                <Trans>Dental practices</Trans>
              </h4>
              <Trans parent="p">
                Collect{' '}
                <AnchorLink
                  href="https://monei.com/monei-pay/accept-payments-dental-practice/"
                  target="_self">
                  dental
                </AnchorLink>{' '}
                co-pays, treatment fees, and follow-up payments without tying up your front desk's
                only card terminal.
              </Trans>
            </UseCaseCard>
          </UseCasesGrid>
        </Section>
      </Content>

      <Content>
        <GradientBox wrapperStyle={{overflow: 'visible', marginTop: '60px'}}>
          <SectionHeader tagName="h3" underline style={{color: 'white'}}>
            <Trans>Ready to accept payments from your phone or tablet?</Trans>
          </SectionHeader>
          <GradientSectionDescription>
            <Trans parent="p">
              Setting up your MONEI account is simple. Create an account, then download the MONEI
              POS app for{' '}
              <AnchorLink href="https://play.google.com/store/apps/details?id=com.monei.moneibusiness">
                Android
              </AnchorLink>{' '}
              or{' '}
              <AnchorLink href="https://apps.apple.com/es/app/monei-pay/id1624647668">
                iOS
              </AnchorLink>{' '}
              on as many devices as your staff needs to start taking payments by{' '}
              <AnchorLink href="https://monei.com/features/tap-to-pay-iphone/" target="_self">
                Tap to Pay on iPhone
              </AnchorLink>
              ,{' '}
              <AnchorLink href="https://monei.com/features/tap-to-phone/" target="_self">
                Tap to Phone on Android
              </AnchorLink>
              , QR code, or payment request.
            </Trans>
          </GradientSectionDescription>
          <SectionActions>
            <ButtonLink href="https://dashboard.monei.com/" variant="dark">
              <Trans>Create an account</Trans>
            </ButtonLink>
          </SectionActions>
          <GradientCtaImage
            sx={{
              maskImage: 'linear-gradient(180deg, #000000 94%, rgba(0, 0, 0, 0) 100%)',
              bottom: '0',
              right: '70px',
              width: '25%'
            }}
            src={integrated_payments_header_image}
          />
        </GradientBox>
      </Content>

      <ReviewsBackground>
        <div style={{display: 'flex', justifyContent: 'center', width: '100%'}}>
          <SectionHeader underline sx={{textAlign: 'center'}}>
            <Trans>What MONEI merchants are saying</Trans>
          </SectionHeader>
        </div>
        <GoogleReviews place={place} />
      </ReviewsBackground>
    </>
  );
};

export default MoneiPay;

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: {ns: {in: ["common", "monei-pay"]}, language: {eq: $language}}) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
    googlePlacesPlace {
      user_ratings_total
      rating
    }
    allGooglePlacesReview(sort: {fields: rating, order: DESC}, filter: {rating: {gt: 2}}) {
      nodes {
        id
        rating
        text
        relative_time_description
        profile_photo_url
        author_name
      }
    }
  }
`;
